// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — mineriumCatalogue.js   ·   WHAT MYSTIC ALLY SELLS
// ────────────────────────────────────────────────────────────────────
// Every furnishing, grower, door, sky and ground in the Minerium, and
// the bundles they come in. Data only — this file imports nothing, has
// no React in it, and never touches Firebase. The till reads it, the
// renderer reads it, the audit reads it, and none of them can disagree
// about a price because there is only one place the price is written.
//
// ── ⭐ BUNDLES ARE THE POINT (caP'n, this session) ──────────────────
//
//     "I don't want to sit there and buy the Super Magical Aethereal
//      Unicorn Pillar and then I have to buy the chair, the rug, the
//      this, the that — let's definitely do bundles."
//
// So a bundle is a FIRST-CLASS CATALOGUE ENTRY, not a discount code
// stapled to a list of items. It has its own id, its own shelf, its own
// price and its own name, and buying one grants everything inside it in
// a single transaction. Individual items still exist for the player who
// wants exactly one more crop bed — but the shop opens on bundles.
//
// ⚑ THE SAVING IS COMPUTED, NEVER TYPED. `bundleValue()` adds up what
// the contents would cost one at a time. A hand-typed "SAVE 30%!" is a
// lie the first time somebody retunes one item's price and forgets the
// bundle — and a shop that lies about a discount is the No-Catfish Law
// (PIERRE_PRIME L27) broken in the most boring way possible.
//
// ── ⭐ THE THREE SHELVES, ENFORCED MECHANICALLY (L23) ───────────────
// caP'n: "everything ever in every game will use that rarity tier —
// even a simple wooden chair seems kind of cool like it's still Fable
// and it's just a chair."
//
//   FABLE      · plain, one flat colour, holographic. The everyday.
//   LEGENDARY  · the best still-holographic there is. ZERO animation.
//   MYTHICAL   · "Lord" — the ASCENDED FORM OF ITS OWN LEGENDARY
//                PREDECESSOR: the same design, plus animation and flare.
//
// ⚠ AND THE MECHANICAL HALF, WHICH IS THE WHOLE REASON THE LAW HOLDS:
// A MYTHICAL CARRIES NO `art` OF ITS OWN — only `ascendedFrom`. The
// renderer resolves a Mythical's shape by walking to its Legendary and
// drawing THAT, then adding the ascension layer on top. So a Mythical
// drifting from its predecessor is not "audited and caught later", it
// is UNREPRESENTABLE. You cannot draw them differently because there is
// only one drawing. `auditCatalogue()` at the bottom closes the rest.
//
// ── EVERYTHING IS PRICED IN ✹ DAWGS ─────────────────────────────────
// Never ◆. THE ECONOMY LAW (L5) says Divine Crests buy ENDGAME ONLY —
// rarest foils, top skills, finite items — and explicitly "never bread."
// A crop bed is bread. Dawgs are what the Minerium runs on, and the only
// way a Crest reaches this shop is by being exchanged into Dawgs at the
// Well first.
// ════════════════════════════════════════════════════════════════════

// ── THE SHELVES ─────────────────────────────────────────────────────
export const SHELVES = ["fable", "legendary", "mythical"];

export const SHELF_META = {
  fable:     { id: "fable",     label: "FABLE",     tint: "#8b8496", order: 0,
               line: "Plain, honest, and holographic anyway." },
  legendary: { id: "legendary", label: "LEGENDARY", tint: "#d4a03c", order: 1,
               line: "The best still thing there is. It does not move, and it does not need to." },
  mythical:  { id: "mythical",  label: "MYTHICAL",  tint: "#a78bfa", order: 2,
               line: "A Legendary that ascended. Same piece, risen." },
};

// ── WHAT A THING DOES ───────────────────────────────────────────────
// The KIND decides which system owns it, and nothing else. A door is a
// prop that happens to be walked through; a grower is a prop that
// happens to pay you. Everything is placed by the same code.
export const KIND = {
  PROP:   "prop",     // pure furnishing
  GROWER: "grower",   // pays Dawgs on a timer
  DOOR:   "door",     // links the Hall and the Yard
  SKY:    "sky",      // one per holding, worn not placed
  GROUND: "ground",   // one per holding, worn not placed
  // ⭐ THE HALL'S FINISHES (2026-09-15). A building game lets you change
  // the floor and the walls; the Hall had hardcoded marble and pale stone.
  // Worn exactly like a sky — one per holding, a visitor sees it — and a
  // finish is a TINT over the same painted stone, so a new one is one row.
  HALL_FLOOR: "hall_floor",
  HALL_WALL:  "hall_wall",
  // ⭐ THE HALL'S LIGHT (OW-34, 2026-09-16). A mood, worn like a finish:
  // it retints the Hall's three fixed lights and every flame in the room.
  // Still three lights — a mood is COLOURS, never a fourth lamp.
  HALL_LIGHT: "hall_light",
  // ⭐ THE HALL'S LAYOUTS (OW-42, 2026-09-16). A whole interior — walls,
  // doorways, paint — drawn once per bay and tiled down every wing. Owned
  // once like a finish, but APPLIED, never worn: `hallInterior.js` owns
  // what applying one means. `layout` names its plan in LAYOUT_PLANS.
  HALL_LAYOUT: "hall_layout",
};

// ⭐ LAYERS (OW-34, 2026-09-16) — what a piece shares a tile WITH.
//   floor — flat coverings (rugs, runners, paths). Furniture stands on
//           them freely, anywhere on them, and they never block a step.
//   stand — ordinary furniture. The default; absent means this.
//   wall  — hung on a wall. Shares its tile with whatever stands below.
// Two pieces only collide when they are on the SAME layer. ⚑ This is why
// a chair on a rug is no longer a "stack": a stack pins one child to the
// dead centre of its base, which is fine on a barrel and absurd on a
// three-tile carpet. Old rows that were stacked on a rug still resolve
// through `on` and keep drawing exactly where they were.
export const LAYER = { FLOOR: "floor", STAND: "stand", WALL: "wall" };
export const LAYERS = [LAYER.FLOOR, LAYER.STAND, LAYER.WALL];

// which holding field a worn kind settles on
// ⚠ HALL_LAYOUT is owned-once but has NO field: it is applied, not worn.
export const WORN_FIELD = { sky: "sky", ground: "ground", hall_floor: "hallFloor", hall_wall: "hallWall", hall_light: "hallLight" };

// Where a thing may be put down. caP'n wanted doors placeable
// "anywhere outside and inside your house", so AREAS is per-item and
// most things allow both.
export const AREAS = { YARD: "yard", HALL: "hall" };
const BOTH = [AREAS.YARD, AREAS.HALL];

// ── ⭐ THE BARN, IN ONE NUMBER ──────────────────────────────────────
// caP'n's ruling: growth "fills up and stops — a barn that holds about
// 8 hours. That's the thing that makes you want to come back."
//
// So no grower carries a hand-written cycle cap. Every cap is derived
// from THIS constant and that grower's own ripen time, which means
// "make the barn bigger" is one number here and not a sweep through
// twenty rows that would inevitably miss one.
export const BARN_HOURS = 8;
export const barnCap = (ripenMs) =>
  Math.max(1, Math.floor((BARN_HOURS * 3600 * 1000) / Math.max(1, ripenMs)));

const MIN = 60 * 1000;

// A grower row, with its cap derived rather than typed.
const grower = (ripenMinutes, yieldUD) => ({
  ripenMs: ripenMinutes * MIN,
  yieldUD,
  capCycles: barnCap(ripenMinutes * MIN),
});

// ════════════════════════════════════════════════════════════════════
// THE ITEMS
// ────────────────────────────────────────────────────────────────────
// Row shape:
//   id, name, kind, shelf, priceUD
//   art          — which recipe in mineriumProps.jsx draws it.
//                  ⚠ MYTHICALS DO NOT HAVE ONE. They carry ascendedFrom.
//   ascendedFrom — Mythical only. The Legendary this is the risen form of.
//   areas        — where it may be placed. Skies/grounds are worn, not placed.
//   footprint    — tiles across, for the placement collision test.
//   size         — [w, d] tiles, for a piece that is NOT square (a 3×1
//                  partition, a 1×3 runner). w runs along x at rot 0, d
//                  along y; a quarter turn swaps them. Absent means
//                  [footprint, footprint]. (OW-34) A non-square piece
//                  turns in quarters everywhere — at 45° it has no tiles.
//   layer        — "floor" · "stand" · "wall" (see LAYER). Absent = stand.
//   passage      — true for an arch: a way THROUGH a wall, said so in the shop.
//   stackTop     — how high something stacked ON this sits. null = nothing
//                  may be stacked on it.
//   walkOver     — true for things you stand ON or walk THROUGH (rugs,
//                  paths, beds, doors). Everything else is SOLID — you walk
//                  around it, in both rooms. (2026-09-15)
//   grow         — growers only.
//   blurb        — Ally's own line. Shown on the tile.
// ════════════════════════════════════════════════════════════════════
export const ITEMS = {

  // ── FABLE · the everyday yard ─────────────────────────────────────
  crop_bed: {
    id: "crop_bed", name: "Crop Bed", kind: KIND.GROWER, shelf: "fable",
    priceUD: 250, art: "crop_bed", areas: [AREAS.YARD], footprint: 1, stackTop: null, walkOver: true,
    grow: grower(5, 6),
    blurb: "Turned soil and a hopeful row. It pays small and it pays often.",
  },
  dawg_bowl: {
    id: "dawg_bowl", name: "Dawg Bowl", kind: KIND.GROWER, shelf: "fable",
    priceUD: 400, art: "dawg_bowl", areas: BOTH, footprint: 1, stackTop: null,
    grow: grower(10, 14),
    blurb: "Leave it out and something always turns up to fill it. Nobody asks what.",
  },
  wooden_chair: {
    id: "wooden_chair", name: "Wooden Chair", kind: KIND.PROP, shelf: "fable",
    priceUD: 110, art: "wooden_chair", areas: BOTH, footprint: 1, stackTop: 0.62,
    blurb: "A chair. It is still Fable, and it is still just a chair.",
  },
  straw_rug: {
    id: "straw_rug", name: "Straw Rug", kind: KIND.PROP, shelf: "fable",
    priceUD: 80, art: "straw_rug", areas: BOTH, footprint: 1, stackTop: null, walkOver: true, layer: LAYER.FLOOR,
    blurb: "Flat, warm, and it makes a floor into a room.",
  },
  fence_post: {
    id: "fence_post", name: "Fence Post", kind: KIND.PROP, shelf: "fable",
    priceUD: 60, art: "fence_post", areas: [AREAS.YARD], footprint: 1, stackTop: null,
    blurb: "One post. A fence is a decision you make eight times.",
  },
  lantern_post: {
    id: "lantern_post", name: "Lantern Post", kind: KIND.PROP, shelf: "fable",
    priceUD: 140, art: "lantern_post", areas: BOTH, footprint: 1, stackTop: null,
    blurb: "Burns without fuel. W'ily says not to think about it.",
  },
  stone_path: {
    id: "stone_path", name: "Stone Path", kind: KIND.PROP, shelf: "fable",
    priceUD: 35, art: "stone_path", areas: BOTH, footprint: 1, stackTop: null, walkOver: true, layer: LAYER.FLOOR,
    blurb: "One slab. Buy twelve and you have somewhere to be going.",
  },
  hay_bale: {
    id: "hay_bale", name: "Hay Bale", kind: KIND.PROP, shelf: "fable",
    priceUD: 90, art: "hay_bale", areas: [AREAS.YARD], footprint: 1, stackTop: 0.72,
    blurb: "Sturdy enough to stand on, which is most of its personality.",
  },
  sapling: {
    id: "sapling", name: "Sapling", kind: KIND.PROP, shelf: "fable",
    priceUD: 120, art: "sapling", areas: [AREAS.YARD], footprint: 1, stackTop: null,
    blurb: "It will not grow. Nothing here grows unless it is paid to.",
  },
  flower_bed: {
    id: "flower_bed", name: "Flower Bed", kind: KIND.PROP, shelf: "fable",
    priceUD: 100, art: "flower_bed", areas: BOTH, footprint: 1, stackTop: null, walkOver: true,
    blurb: "Six colours that do not occur anywhere on Krush.",
  },
  barrel: {
    id: "barrel", name: "Barrel", kind: KIND.PROP, shelf: "fable",
    priceUD: 85, art: "barrel", areas: BOTH, footprint: 1, stackTop: 0.86,
    blurb: "Sealed. Ally will not say by whom.",
  },
  torch: {
    id: "torch", name: "Torch", kind: KIND.PROP, shelf: "fable",
    priceUD: 70, art: "torch", areas: BOTH, footprint: 1, stackTop: null,
    blurb: "Put it on top of something. See what happens. That is the whole toy.",
  },
  bird_bath: {
    id: "bird_bath", name: "Void-Gull Bath", kind: KIND.PROP, shelf: "fable",
    priceUD: 160, art: "bird_bath", areas: [AREAS.YARD], footprint: 1, stackTop: null,
    blurb: "The gulls out past the pier will find it. They find everything.",
  },
  sign_post: {
    id: "sign_post", name: "Sign Post", kind: KIND.PROP, shelf: "fable",
    priceUD: 75, art: "sign_post", areas: BOTH, footprint: 1, stackTop: null,
    blurb: "Points somewhere. Which way is up to you and gravity.",
  },
  plank_door: {
    id: "plank_door", name: "Plank Door", kind: KIND.DOOR, shelf: "fable",
    priceUD: 0, art: "plank_door", areas: BOTH, footprint: 1, stackTop: null, walkOver: true,
    blurb: "Your first door. Free, because a holding you cannot get into is not a holding.",
  },
  sky_default: {
    id: "sky_default", name: "Open Unstable Sky", kind: KIND.SKY, shelf: "fable",
    priceUD: 0, art: "sky_default", areas: [],
    blurb: "What is out there anyway, when nobody has decided otherwise.",
  },
  sky_dusk: {
    id: "sky_dusk", name: "Long Dusk", kind: KIND.SKY, shelf: "fable",
    priceUD: 900, art: "sky_dusk", areas: [],
    blurb: "The hour before the eclipse cracks. It never gets past it.",
  },
  // ⭐⭐ THE REMASTER (caP'n, 2026-09-25): "remake the exact same sky but …
  // way better in blender … a ps1 game getting a ps3 remaster … you can buy
  // this one from Bonnie's Bunni Basket … Fable Tier." Open Unstable Sky
  // stays the free standard one to switch back to.
  // ⚑ ONE SKY, WORN EVERYWHERE (same day): "when you use one sky, it should
  // also appear in the unstable pier (and eventually any other game where
  // you can customize the sky)". So this is an ordinary SKY — worn in the
  // holding's `sky` field and drawn by every horizon from `ieSkies.jsx`.
  // (The id keeps its first name; ids are never renamed once shipped.)
  pier_sky_remaster: {
    id: "pier_sky_remaster", name: "Open Unstable Sky · Remastered", kind: KIND.SKY, shelf: "fable",
    priceUD: 900, art: "pier_sky_remaster", areas: [],
    blurb: "The same sky — same eclipse, same crack, same red hole — only now you can see the gas in the bands and the glow in the wound. Worn over your Minerium AND the pier.",
  },
  ground_grass: {
    id: "ground_grass", name: "Pale Grass", kind: KIND.GROUND, shelf: "fable",
    priceUD: 0, art: "ground_grass", areas: [],
    blurb: "It grew here before you did.",
  },
  ground_dust: {
    id: "ground_dust", name: "Shatter Dust", kind: KIND.GROUND, shelf: "fable",
    priceUD: 500, art: "ground_dust", areas: [],
    blurb: "Ground-down pocket dimension. Drains beautifully.",
  },

  // ── ⭐ OW-34 · THE ARCHITECT'S FABLE — rooms out of one long hall ──
  // Partitions are SOLID (you walk round them); arches are the gaps you
  // walk THROUGH. Rugs are the floor layer. Wall pieces hang (Hall only).
  parlour_rug: {
    id: "parlour_rug", name: "Parlour Rug", kind: KIND.PROP, shelf: "fable",
    priceUD: 260, art: "parlour_rug", areas: BOTH, footprint: 2, stackTop: null,
    walkOver: true, layer: LAYER.FLOOR,
    blurb: "Four tiles of somewhere to be. Put a chair on it anywhere you like.",
  },
  hall_runner: {
    id: "hall_runner", name: "Hall Runner", kind: KIND.PROP, shelf: "fable",
    priceUD: 180, art: "hall_runner", areas: BOTH, footprint: 1, size: [1, 3], stackTop: null,
    walkOver: true, layer: LAYER.FLOOR,
    blurb: "Three tiles long and going somewhere. Lay two end to end and it is a corridor.",
  },
  folding_screen: {
    id: "folding_screen", name: "Folding Screen", kind: KIND.PROP, shelf: "fable",
    priceUD: 240, art: "folding_screen", areas: BOTH, footprint: 1, size: [2, 1], stackTop: null,
    blurb: "Three painted leaves. The cheapest wall there is, and the easiest to change your mind about.",
  },
  timber_arch: {
    id: "timber_arch", name: "Timber Arch", kind: KIND.PROP, shelf: "fable",
    priceUD: 150, art: "timber_arch", areas: BOTH, footprint: 1, stackTop: null, walkOver: true, passage: true,
    blurb: "A doorway with no door. Stand it in a gap between screens and the gap becomes a way in.",
  },
  hedge_row: {
    id: "hedge_row", name: "Hedge Row", kind: KIND.PROP, shelf: "fable",
    priceUD: 200, art: "hedge_row", areas: [AREAS.YARD], footprint: 1, size: [2, 1], stackTop: null,
    blurb: "A yard's own partition. It was clipped square before you bought it and it stays that way.",
  },
  wall_banner: {
    id: "wall_banner", name: "House Banner", kind: KIND.PROP, shelf: "fable",
    priceUD: 150, art: "wall_banner", areas: [AREAS.HALL], footprint: 1, stackTop: null,
    walkOver: true, layer: LAYER.WALL,
    blurb: "Your colours, on your wall. Hangs from any plain stretch of stone.",
  },
  wall_sconce: {
    id: "wall_sconce", name: "Wall Sconce", kind: KIND.PROP, shelf: "fable",
    priceUD: 120, art: "wall_sconce", areas: [AREAS.HALL], footprint: 1, stackTop: null,
    walkOver: true, layer: LAYER.WALL,
    blurb: "An iron arm and a flame that takes the room's mood with it.",
  },
  wall_shelf: {
    id: "wall_shelf", name: "Curio Shelf", kind: KIND.PROP, shelf: "fable",
    priceUD: 170, art: "wall_shelf", areas: [AREAS.HALL], footprint: 1, stackTop: null,
    walkOver: true, layer: LAYER.WALL,
    blurb: "Two jars and three books nobody has opened. It looks lived in, which is the point.",
  },

  // ── ⭐ ROOM FURNITURE (caP'n, 2026-09-16: "all of it") ────────────
  // A divided room needs somewhere to eat, read and sleep.
  plank_table: {
    id: "plank_table", name: "Plank Table", kind: KIND.PROP, shelf: "fable",
    priceUD: 220, art: "plank_table", areas: BOTH, footprint: 1, size: [2, 1], stackTop: 0.76,
    blurb: "Two tiles of scrubbed board. Put a torch on it and it is dinner.",
  },
  tall_bookcase: {
    id: "tall_bookcase", name: "Tall Bookcase", kind: KIND.PROP, shelf: "fable",
    priceUD: 260, art: "tall_bookcase", areas: [AREAS.HALL], footprint: 1, size: [2, 1], stackTop: null,
    blurb: "Four shelves of spines. Two of the books are real. Nobody knows which two.",
  },
  straw_bed: {
    id: "straw_bed", name: "Straw Bed", kind: KIND.PROP, shelf: "fable",
    priceUD: 300, art: "straw_bed", areas: [AREAS.HALL], footprint: 1, size: [1, 2], stackTop: null,
    blurb: "A tick of clean straw and a wool blanket. Better sleep than the pier has ever offered.",
  },

  // ── THE HALL'S FINISHES · worn, like a sky ────────────────────────
  // ⭐ (caP'n, 2026-09-16: "very unique from each other, not just slightly
  // different colors") `finish.pattern` picks a DIFFERENT painted surface —
  // polished marble, split slate flags, marble with living Anima veins,
  // dressed stone, trowelled plaster with a frieze, cracked N'ullus stone
  // that glows. `tint` is now the SWATCH the panel shows (the painted
  // surface carries its own colour); `runner` still tints the carpet.
  floor_marble: {
    id: "floor_marble", name: "White Marble", kind: KIND.HALL_FLOOR, shelf: "fable",
    priceUD: 0, art: "floor_marble", areas: [], finish: { pattern: "marble", tint: "#4a3e58", runner: "#ffffff" },
    blurb: "The floor the Hall was laid with. It has seen every deed you own.",
  },
  floor_slate: {
    id: "floor_slate", name: "Void Slate", kind: KIND.HALL_FLOOR, shelf: "fable",
    priceUD: 700, art: "floor_slate", areas: [], finish: { pattern: "slate", tint: "#6a6480", runner: "#8a7ab0" },
    blurb: "Darker underfoot. Trophies read louder against it.",
  },
  floor_anima: {
    id: "floor_anima", name: "Anima-Veined Marble", kind: KIND.HALL_FLOOR, shelf: "legendary",
    priceUD: 4200, art: "floor_anima", areas: [], finish: { pattern: "anima", tint: "#ffd2e4", runner: "#ff7fb0" },
    blurb: "Pink runs through the white like something is still alive down there.",
  },
  wall_stone: {
    id: "wall_stone", name: "Pale Stone", kind: KIND.HALL_WALL, shelf: "fable",
    priceUD: 0, art: "wall_stone", areas: [], finish: { pattern: "stone", tint: "#d8d0e2" },
    blurb: "The portico's own stone, inside and out.",
  },
  wall_dusk: {
    id: "wall_dusk", name: "Dusk Plaster", kind: KIND.HALL_WALL, shelf: "fable",
    priceUD: 700, art: "wall_dusk", areas: [], finish: { pattern: "plaster", tint: "#c9a08a" },
    blurb: "Warm, like the hour before the eclipse cracks.",
  },
  wall_nullus: {
    id: "wall_nullus", name: "N'ullus Violet", kind: KIND.HALL_WALL, shelf: "legendary",
    priceUD: 4200, art: "wall_nullus", areas: [], finish: { pattern: "nullus", tint: "#9a86c8" },
    blurb: "The colour of the crack in the sky, brought indoors.",
  },

  // ── ⭐ THE HALL'S LAYOUTS · owned once, applied (OW-42) ───────────
  // Time and design, sold: the whole inside of the Hall rebuilt in one
  // press. Plans live in hallInterior.js (LAYOUT_PLANS), by `layout`.
  layout_enfilade: {
    id: "layout_enfilade", name: "The Enfilade", kind: KIND.HALL_LAYOUT, shelf: "fable",
    priceUD: 2400, art: "layout", layout: "enfilade", areas: [],
    blurb: "Every wing its own room, and one door lined up through all of them.",
  },
  layout_twin_parlours: {
    id: "layout_twin_parlours", name: "Twin Parlours", kind: KIND.HALL_LAYOUT, shelf: "fable",
    priceUD: 2400, art: "layout", layout: "twin_parlours", areas: [],
    blurb: "A dark-floored parlour to the north, the deeds' long room to the south.",
  },
  layout_long_gallery: {
    id: "layout_long_gallery", name: "The Long Gallery", kind: KIND.HALL_LAYOUT, shelf: "fable",
    priceUD: 2400, art: "layout", layout: "long_gallery", areas: [],
    blurb: "One slate corridor the length of the Hall, with alcoves either side.",
  },
  layout_cloister: {
    id: "layout_cloister", name: "The Cloister", kind: KIND.HALL_LAYOUT, shelf: "legendary",
    priceUD: 7200, art: "layout", layout: "cloister", areas: [],
    blurb: "A walled court of living marble in every wing, doors on all four sides.",
  },
  layout_chambered: {
    id: "layout_chambered", name: "The Chambered Hall", kind: KIND.HALL_LAYOUT, shelf: "legendary",
    priceUD: 7200, art: "layout", layout: "chambered", areas: [],
    blurb: "Ten rooms where there was one. Wander it like a house, not a corridor.",
  },
  layout_labyrinth: {
    id: "layout_labyrinth", name: "N'ullus Labyrinth", kind: KIND.HALL_LAYOUT, shelf: "legendary",
    priceUD: 7200, art: "layout", layout: "labyrinth", areas: [],
    blurb: "Violet walls that fold back on themselves. Your deeds wait at the turns.",
  },
  layout_risen_cloister: {
    id: "layout_risen_cloister", name: "The Cloister, Risen", kind: KIND.HALL_LAYOUT, shelf: "mythical",
    priceUD: 16000, ascendedFrom: "layout_cloister", areas: [],
    blurb: "The same court — but the walls breathe light, slow as a sleeping thing.",
  },
  layout_risen_labyrinth: {
    id: "layout_risen_labyrinth", name: "The Labyrinth Awake", kind: KIND.HALL_LAYOUT, shelf: "mythical",
    priceUD: 16000, ascendedFrom: "layout_labyrinth", areas: [],
    blurb: "The maze is lit from inside its walls. It knows you are in it.",
  },

  // ── ⭐ THE HALL'S LIGHT · worn, like a finish (OW-34) ─────────────
  // `mood` retints the Hall's THREE fixed lights, the fog and every flame.
  //   hemi: [sky, ground, intensity] · key/fill: [colour, intensity]
  // Candlelight is the room as it has always been lit, number for number.
  light_candle: {
    id: "light_candle", name: "Candlelight", kind: KIND.HALL_LIGHT, shelf: "fable",
    priceUD: 0, art: "light_candle", areas: [],
    mood: { hemi: ["#a89cb8", "#6a4a2c", 0.62], key: ["#ffdca4", 1.15], fill: ["#B388FF", 0.42],
            fog: "#0e0a16", flame: "#ffb84d", pool: "#ffcf8a",
            atmos: { kind: "dust", color: "#ffd9a0", count: 90 }, rays: "#c8b8ff", fogNear: 18, fogFar: 62 },
    blurb: "The Hall as it was lit the day it was built. Warm, and a little violet in the corners.",
  },
  light_moon: {
    id: "light_moon", name: "Moonlit Vigil", kind: KIND.HALL_LIGHT, shelf: "fable",
    priceUD: 800, art: "light_moon", areas: [],
    mood: { hemi: ["#8aa0d8", "#2a2a44", 0.58], key: ["#b8cdfa", 0.95], fill: ["#6a8cff", 0.5],
            fog: "#0a0e1c", flame: "#9fd0ff", pool: "#b8d8ff",
            atmos: { kind: "frost", color: "#dff0ff", count: 160 }, rays: "#bfe0ff", rayBoost: 2.4, fogNear: 10, fogFar: 44 },
    blurb: "Every flame burns blue and nobody minds. The deeds look older in it.",
  },
  light_ember: {
    id: "light_ember", name: "Ember Hour", kind: KIND.HALL_LIGHT, shelf: "legendary",
    priceUD: 4600, art: "light_ember", areas: [],
    mood: { hemi: ["#d89a70", "#4a200c", 0.6], key: ["#ffb070", 1.25], fill: ["#ff5a3c", 0.38],
            fog: "#160a08", flame: "#ff8a3a", pool: "#ffb070",
            atmos: { kind: "embers", color: "#ff7a2a", count: 140 }, rays: "#ffb080", rayBoost: 0.4, fogNear: 14, fogFar: 54 },
    blurb: "The hour a hearth is at its best, kept there on purpose.",
  },

  // ── LEGENDARY · still, and the best still thing there is ──────────
  chao_pool: {
    id: "chao_pool", name: "Chao Pool", kind: KIND.GROWER, shelf: "legendary",
    priceUD: 3000, art: "chao_pool", areas: [AREAS.YARD], footprint: 2, stackTop: null,
    grow: grower(15, 40),
    blurb: "A cupful of the ocean the pier is built over. It pays, and it watches.",
  },
  anima_tree: {
    id: "anima_tree", name: "Anima Tree", kind: KIND.GROWER, shelf: "legendary",
    priceUD: 6500, art: "anima_tree", areas: [AREAS.YARD], footprint: 2, stackTop: null,
    grow: grower(30, 95),
    blurb: "Roots into a River of Anima nobody can find the surface of.",
  },
  crest_fountain: {
    id: "crest_fountain", name: "Crest Fountain", kind: KIND.PROP, shelf: "legendary",
    priceUD: 2200, art: "crest_fountain", areas: BOTH, footprint: 2, stackTop: null,
    blurb: "A dry fountain in the shape of the Well. It is a compliment, not a copy.",
  },
  void_bench: {
    id: "void_bench", name: "Void Bench", kind: KIND.PROP, shelf: "legendary",
    priceUD: 1400, art: "void_bench", areas: BOTH, footprint: 2, stackTop: 0.66,
    blurb: "Sit long enough and the seat forgets which way is down. It is fine.",
  },
  star_lamp: {
    id: "star_lamp", name: "Star Lamp", kind: KIND.PROP, shelf: "legendary",
    priceUD: 1100, art: "star_lamp", areas: BOTH, footprint: 1, stackTop: null,
    blurb: "One real star, kept small on purpose.",
  },
  pale_pillar: {
    id: "pale_pillar", name: "Pale Pillar", kind: KIND.PROP, shelf: "legendary",
    priceUD: 1800, art: "pale_pillar", areas: BOTH, footprint: 1, stackTop: 1.55,
    blurb: "Holds nothing up. Holds it up perfectly.",
  },
  brass_door: {
    id: "brass_door", name: "Brass Door", kind: KIND.DOOR, shelf: "legendary",
    priceUD: 2400, art: "brass_door", areas: BOTH, footprint: 1, stackTop: null, walkOver: true,
    blurb: "An entrance that says somebody lives here on purpose.",
  },
  grand_carpet: {
    id: "grand_carpet", name: "Grand Carpet", kind: KIND.PROP, shelf: "legendary",
    priceUD: 3600, art: "grand_carpet", areas: BOTH, footprint: 3, stackTop: null,
    walkOver: true, layer: LAYER.FLOOR,
    blurb: "Nine tiles of gold-edged violet. A room is wherever this is.",
  },
  stone_partition: {
    id: "stone_partition", name: "Stone Partition", kind: KIND.PROP, shelf: "legendary",
    priceUD: 2600, art: "stone_partition", areas: BOTH, footprint: 1, size: [3, 1], stackTop: null,
    blurb: "Three tiles of the Hall's own stone, gold-railed. It was always going to be a wall.",
  },
  pale_archway: {
    id: "pale_archway", name: "Pale Archway", kind: KIND.PROP, shelf: "legendary",
    priceUD: 1600, art: "pale_archway", areas: BOTH, footprint: 1, stackTop: null, walkOver: true, passage: true,
    blurb: "Fluted, keystoned and completely serious about being a doorway.",
  },
  pale_hearth: {
    id: "pale_hearth", name: "Pale Hearth", kind: KIND.PROP, shelf: "legendary",
    priceUD: 3800, art: "pale_hearth", areas: [AREAS.HALL], footprint: 1, size: [2, 1], stackTop: null,
    blurb: "A fireplace in the Hall's own stone. Its fire burns whatever colour the room is lit.",
  },
  void_four_poster: {
    id: "void_four_poster", name: "Void Four-Poster", kind: KIND.PROP, shelf: "legendary",
    priceUD: 4800, art: "void_four_poster", areas: [AREAS.HALL], footprint: 1, size: [2, 3], stackTop: null,
    blurb: "Four black posts and a canopy the colour of the crack in the sky.",
  },
  long_table: {
    id: "long_table", name: "Long Table", kind: KIND.PROP, shelf: "legendary",
    priceUD: 2400, art: "long_table", areas: BOTH, footprint: 1, size: [3, 1], stackTop: null,
    blurb: "Dark wood, gold candelabra, seats for everyone you have ever beaten at cards.",
  },
  grand_tapestry: {
    id: "grand_tapestry", name: "Grand Tapestry", kind: KIND.PROP, shelf: "legendary",
    priceUD: 3900, art: "grand_tapestry", areas: [AREAS.HALL], footprint: 1, size: [2, 1], stackTop: null,
    walkOver: true, layer: LAYER.WALL,
    blurb: "The Unstable World in thread: the cracked eclipse, and the pier underneath it.",
  },
  sky_eclipse: {
    id: "sky_eclipse", name: "Cracked Eclipse", kind: KIND.SKY, shelf: "legendary",
    priceUD: 5200, art: "sky_eclipse", areas: [],
    blurb: "Rimmed in O'mnis. Cracked by N'ullus. Directly overhead, always.",
  },
  // ⭐⭐ THE EYE (caP'n, 2026-09-25) — the whole sky is one galaxy iris, the
  // pupil a black hole overhead. Legendary: it is magnificent and it is STILL
  // (L23 — only a Mythical moves). Worn everywhere, like every sky (L96).
  sky_eye: {
    id: "sky_eye", name: "The Eye", kind: KIND.SKY, shelf: "legendary",
    priceUD: 5200, art: "sky_eye", areas: [],
    blurb: "Look up. That is not a black hole. Try not to be the thing it is looking at.",
  },
  ground_anima_moss: {
    id: "ground_anima_moss", name: "Anima Moss", kind: KIND.GROUND, shelf: "legendary",
    priceUD: 3400, art: "ground_anima_moss", areas: [],
    blurb: "Soft, faintly lit, and it remembers where you walked yesterday.",
  },

  // ── MYTHICAL · a Legendary that ascended ──────────────────────────
  // ⚠ NO `art` ON ANY OF THESE. The renderer draws `ascendedFrom` and
  //   layers the ascension over it, so "same design, risen" is a fact
  //   about the code rather than a promise in a document.
  aethereal_pillar: {
    id: "aethereal_pillar", name: "Super Magical Aethereal Pillar", kind: KIND.PROP,
    shelf: "mythical", priceUD: 42000, ascendedFrom: "pale_pillar",
    areas: BOTH, footprint: 1, stackTop: 1.55,
    blurb: "The Pale Pillar, risen. It was named before it existed, and it grew into the name.",
  },
  throne_of_dawgs: {
    id: "throne_of_dawgs", name: "Throne of Dawgs", kind: KIND.PROP,
    shelf: "mythical", priceUD: 58000, ascendedFrom: "void_bench",
    areas: BOTH, footprint: 2, stackTop: 0.66,
    blurb: "The Void Bench, risen. It is still a bench. It has simply stopped apologising.",
  },
  rift_door: {
    id: "rift_door", name: "Rift Door", kind: KIND.DOOR,
    shelf: "mythical", priceUD: 36000, ascendedFrom: "brass_door",
    areas: BOTH, footprint: 1, stackTop: null, walkOver: true,
    blurb: "The Brass Door, risen. It opens before you reach it.",
  },
  carpet_of_crests: {
    id: "carpet_of_crests", name: "Carpet of Crests", kind: KIND.PROP,
    shelf: "mythical", priceUD: 48000, ascendedFrom: "grand_carpet",
    areas: BOTH, footprint: 3, stackTop: null, walkOver: true, layer: LAYER.FLOOR,
    blurb: "The Grand Carpet, risen. The crests in the weave turn to watch you cross.",
  },
  nullus_veil: {
    id: "nullus_veil", name: "Veil of N'ullus", kind: KIND.PROP,
    shelf: "mythical", priceUD: 52000, ascendedFrom: "stone_partition",
    areas: BOTH, footprint: 1, size: [3, 1], stackTop: null,
    blurb: "The Stone Partition, risen. A seam of the cracked sky runs through it, breathing.",
  },
  living_tapestry: {
    id: "living_tapestry", name: "Living Tapestry", kind: KIND.PROP,
    shelf: "mythical", priceUD: 46000, ascendedFrom: "grand_tapestry",
    areas: [AREAS.HALL], footprint: 1, size: [2, 1], stackTop: null, walkOver: true, layer: LAYER.WALL,
    blurb: "The Grand Tapestry, risen. The sea in it moves. The eclipse in it does not, yet.",
  },
  hearth_of_the_rise: {
    id: "hearth_of_the_rise", name: "Hearth of the Rise", kind: KIND.PROP,
    shelf: "mythical", priceUD: 44000, ascendedFrom: "pale_hearth",
    areas: [AREAS.HALL], footprint: 1, size: [2, 1], stackTop: null,
    blurb: "The Pale Hearth, risen. Something in the fire is climbing out of it, slowly, forever.",
  },
  dreaming_bed: {
    id: "dreaming_bed", name: "Bed of Unending Dusk", kind: KIND.PROP,
    shelf: "mythical", priceUD: 52000, ascendedFrom: "void_four_poster",
    areas: [AREAS.HALL], footprint: 1, size: [2, 3], stackTop: null,
    blurb: "The Void Four-Poster, risen. Sleep in it and the eclipse never cracks.",
  },
  light_anima: {
    id: "light_anima", name: "Anima Bloom", kind: KIND.HALL_LIGHT,
    shelf: "mythical", priceUD: 38000, ascendedFrom: "light_ember", areas: [],
    blurb: "Ember Hour, risen. The whole room breathes pink, slowly, like something asleep in the walls.",
  },
  lyt_spire: {
    id: "lyt_spire", name: "Lyt Spire", kind: KIND.GROWER,
    shelf: "mythical", priceUD: 75000, ascendedFrom: "anima_tree",
    areas: [AREAS.YARD], footprint: 2, stackTop: null,
    grow: grower(60, 260),
    blurb: "The Anima Tree, risen — and now it radiates instead of drinking.",
  },
  sky_the_rise: {
    id: "sky_the_rise", name: "THE RISE", kind: KIND.SKY,
    shelf: "mythical", priceUD: 90000, ascendedFrom: "sky_eclipse",
    areas: [],
    blurb: "The Cracked Eclipse, risen. Do not look at it for very long.",
  },
};

export const ITEM_IDS = Object.keys(ITEMS);

// ── RESOLUTION ──────────────────────────────────────────────────────
// The one function that knows how a Mythical is drawn. Everything that
// wants a shape asks THIS, never `item.art` directly — that is the
// single point of enforcement for "a Mythical is its Legendary, risen."
export function artOf(itemId) {
  const it = ITEMS[itemId];
  if (!it) return null;
  if (it.art) return it.art;
  if (it.ascendedFrom) return artOf(it.ascendedFrom);
  return null;
}

// True when this row should be drawn with the ascension layer on top.
export const isAscended = (itemId) => !!ITEMS[itemId]?.ascendedFrom;

// ⭐ SHAPE AND LAYER (OW-34). Data questions, answered here so the engine,
// both renderers and the shed can never disagree about them.
export const layerOf = (itemId) => {
  const l = ITEMS[itemId]?.layer;
  return LAYERS.includes(l) ? l : LAYER.STAND;
};
// [w, d] at rot 0
export function sizeOf(itemId) {
  const it = ITEMS[itemId];
  if (!it) return [1, 1];
  if (Array.isArray(it.size) && it.size.length === 2) return [Math.max(1, it.size[0] | 0), Math.max(1, it.size[1] | 0)];
  const f = Math.max(1, it.footprint || 1);
  return [f, f];
}
// A piece that must turn in quarters: anything non-square (at 45° it
// covers no whole tiles) and anything hung on a wall (a wall is square to
// the room).
export const turnsInQuarters = (itemId) => {
  const [w, d] = sizeOf(itemId);
  return w !== d || layerOf(itemId) === LAYER.WALL;
};
// "2×1" — what the shed prints
export const sizeLabel = (itemId) => { const [w, d] = sizeOf(itemId); return `${w}×${d}`; };

// ⭐ A LIGHT'S MOOD — resolved the Mythical way: a risen light carries no
// mood of its own, it wears its Legendary's and adds `pulse`.
export function moodOf(itemId) {
  const it = ITEMS[itemId];
  if (!it || it.kind !== KIND.HALL_LIGHT) return null;
  if (it.mood) return it.mood;
  const base = it.ascendedFrom ? moodOf(it.ascendedFrom) : null;
  // risen: the embers become drifting Anima petals, the rays turn rose
  return base ? { ...base, pulse: true, bloom: "#ff7fb0", rays: "#ffb0d8", rayBoost: 1.6,
                  atmos: { kind: "petals", color: "#ff9ac8", count: 120 } } : null;
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE TESTING TILL  —  L52 · OW-21 · caP'n's ruling, 2026-09-13
// ────────────────────────────────────────────────────────────────────
//   **"Purchasable for 1 single UP Dawg — testing everything is always
//     1 currency of the lowest amount."**
//
// While a title is in testing, EVERY price in it becomes one unit of
// that title's lowest currency. For the Minerium that is ✹1. The
// sticker price stays exactly as written below and is still SHOWN —
// struck through, beside what you actually pay — so nobody is surprised
// on the day testing ends and the real number arrives.
//
// ⚑ ONE CONST, ONE FUNCTION. This is `markShop.js`'s shape, moved here
// because `priceOf()` lives here and the whole point of a single price
// function is that the tag and the till cannot disagree. Do NOT scatter
// this decision — a shelf that reads ✹1 in the shop and charges ✹16,500
// at the counter is how a storefront lies to a player.
//
// ⚠ THE PAIR. `priceOf()` is WHAT YOU PAY. `listPriceOf()` is WHAT IT
// IS WORTH. Anything that shows money to a player wants the first;
// anything that reasons about VALUE — bundle savings, receipts, a
// future resale — wants the second. Mixing them is the only way to get
// this wrong, and it is why they are named so differently.
//
// TO END TESTING: flip `TESTING` to false. That is the entire change.
// ════════════════════════════════════════════════════════════════════
export const TESTING = true;
export const TESTING_PRICE_UD = 1;

// What a piece IS WORTH — the sticker, always, testing or not.
export const listPriceOf = (itemId) => {
  const it = ITEMS[itemId];
  return it ? (Number(it.priceUD) || 0) : null;
};

// ⭐ What a piece COSTS RIGHT NOW. Every price the shop shows and every
// price it charges comes through this one function.
// ⚠ FREE STAYS FREE. A ✹0 row (the plank door, the default sky, the
// starting ground) is free BY DESIGN — THE FREE SHELF, L30 — and the
// testing till must never turn a free thing into a ✹1 thing. A player
// who cannot afford ✹1 on a brand-new account is exactly who that shelf
// exists for.
export const priceOf = (itemId) => {
  const list = listPriceOf(itemId);
  if (list === null) return null;
  if (list === 0) return 0;
  return TESTING ? TESTING_PRICE_UD : list;
};

export const itemsOnShelf = (shelf) =>
  ITEM_IDS.map((id) => ITEMS[id]).filter((i) => i.shelf === shelf);

export const itemsOfKind = (kind) =>
  ITEM_IDS.map((id) => ITEMS[id]).filter((i) => i.kind === kind);

// A thing you WEAR (one per holding) rather than PLACE.
// ⚑ OW-42: a layout is owned once too (bought like a finish, never stocked,
// never placed) — `isWearable` is the narrower question wearItem asks.
export const isWorn = (itemId) => {
  const k = ITEMS[itemId]?.kind;
  return k === KIND.SKY || k === KIND.GROUND || k === KIND.HALL_FLOOR || k === KIND.HALL_WALL || k === KIND.HALL_LIGHT || k === KIND.HALL_LAYOUT;
};
export const isWearable = (itemId) => isWorn(itemId) && ITEMS[itemId]?.kind !== KIND.HALL_LAYOUT;

// ════════════════════════════════════════════════════════════════════
// THE BUNDLES  —  what the shop opens on
// ────────────────────────────────────────────────────────────────────
// `contents` is { itemId: count }. Buying grants every line at once, in
// one transaction, for one price.
// ════════════════════════════════════════════════════════════════════
export const BUNDLES = {
  bundle_first_acre: {
    id: "bundle_first_acre", name: "The First Acre", shelf: "fable", priceUD: 1200,
    blurb: "Everything a holding needs to stop being a field. Ally will not sell you a chair " +
           "on its own until you have one of these, and she is right not to.",
    contents: { crop_bed: 3, fence_post: 8, lantern_post: 2, sapling: 2, straw_rug: 1, wooden_chair: 1 },
  },
  bundle_the_yard: {
    id: "bundle_the_yard", name: "The Yard, Furnished", shelf: "fable", priceUD: 1150,
    blurb: "The clutter that makes a place look lived in. Twelve slabs of path is not a lot of path.",
    contents: { stone_path: 12, hay_bale: 4, barrel: 3, flower_bed: 4, bird_bath: 1, sign_post: 1, torch: 2 },
  },
  bundle_growers_row: {
    id: "bundle_growers_row", name: "The Growers' Row", shelf: "legendary", priceUD: 16500,
    blurb: "Two pools, two trees, and six more beds. This is the bundle that stops you " +
           "having to be here for the money to arrive.",
    contents: { chao_pool: 2, anima_tree: 2, crop_bed: 6, dawg_bowl: 1 },
  },
  bundle_the_parlour: {
    id: "bundle_the_parlour", name: "The Parlour", shelf: "legendary", priceUD: 8900,
    blurb: "For inside. Benches, lamps, pillars and a fountain that has never once been wet.",
    contents: { void_bench: 2, star_lamp: 3, pale_pillar: 2, crest_fountain: 1, straw_rug: 2 },
  },
  // ⭐ OW-34 — the two remodel crates. A room is screens, a way through,
  // something underfoot and something on the walls; these are exactly that.
  bundle_drawing_room: {
    id: "bundle_drawing_room", name: "The Drawing Room", shelf: "fable", priceUD: 1500,
    blurb: "Two screens, an arch between them, a rug and a runner, and something for the walls. " +
           "Enough to turn one end of the Hall into a room with a way in.",
    contents: { folding_screen: 2, timber_arch: 1, parlour_rug: 1, hall_runner: 2, wall_banner: 2, wall_sconce: 2, wall_shelf: 1 },
  },
  bundle_the_architect: {
    id: "bundle_the_architect", name: "The Architect's Crate", shelf: "legendary", priceUD: 9400,
    blurb: "Stone walls, arches, carpets and a tapestry. Ally calls it a remodel. " +
           "W'ily calls it moving house without leaving.",
    contents: { stone_partition: 2, folding_screen: 2, timber_arch: 2, parlour_rug: 2, hall_runner: 2,
                wall_banner: 3, wall_sconce: 4, wall_shelf: 2, grand_tapestry: 1 },
  },
  // ⭐ (caP'n, 2026-09-16: "double the amount of bundles") — seven more, one
  // for every kind of room a player is now able to make.
  bundle_reading_nook: {
    id: "bundle_reading_nook", name: "The Reading Nook", shelf: "fable", priceUD: 700,
    blurb: "A bookcase, a table, two chairs and a light to read by. Every Hall needs a quiet corner.",
    contents: { tall_bookcase: 1, plank_table: 1, wooden_chair: 2, straw_rug: 1, wall_sconce: 1 },
  },
  bundle_bedchamber: {
    id: "bundle_bedchamber", name: "The Bedchamber", shelf: "fable", priceUD: 880,
    blurb: "A bed, a screen for it, a runner to it and a shelf above it. You live here now.",
    contents: { straw_bed: 1, folding_screen: 1, hall_runner: 1, wall_shelf: 1, wall_sconce: 2 },
  },
  bundle_kitchen_garden: {
    id: "bundle_kitchen_garden", name: "The Kitchen Garden", shelf: "fable", priceUD: 1900,
    blurb: "Beds in hedged rows, a path between them and a table to eat what grows. It pays, too.",
    contents: { crop_bed: 4, hedge_row: 4, stone_path: 8, barrel: 2, plank_table: 1 },
  },
  bundle_hearth_room: {
    id: "bundle_hearth_room", name: "The Hearth Room", shelf: "legendary", priceUD: 11500,
    blurb: "A fire, a carpet in front of it, a long table and benches. The warmest room in the Unstable World.",
    contents: { pale_hearth: 1, grand_carpet: 1, long_table: 1, void_bench: 2, star_lamp: 2 },
  },
  bundle_gallery_hang: {
    id: "bundle_gallery_hang", name: "The Gallery Hang", shelf: "legendary", priceUD: 12200,
    blurb: "Two tapestries, banners, sconces and the archways to walk between them.",
    contents: { grand_tapestry: 2, wall_banner: 4, wall_sconce: 4, pale_archway: 2, pale_pillar: 2 },
  },
  bundle_great_bedchamber: {
    id: "bundle_great_bedchamber", name: "The Great Bedchamber", shelf: "legendary", priceUD: 11000,
    blurb: "A four-poster, stone walls around it, a carpet under it and books beside it.",
    contents: { void_four_poster: 1, stone_partition: 2, grand_carpet: 1, tall_bookcase: 2, wall_sconce: 2 },
  },
  bundle_risen_hall: {
    id: "bundle_risen_hall", name: "The Risen Hall", shelf: "mythical", priceUD: 250000,
    blurb: "The Hall's risen set, and the light to see it by: the Carpet of Crests, two Veils, " +
           "the Living Tapestry, the Hearth of the Rise, the Bed of Unending Dusk and Anima Bloom.",
    contents: { carpet_of_crests: 1, nullus_veil: 2, living_tapestry: 1, hearth_of_the_rise: 1, dreaming_bed: 1, light_anima: 1 },
  },
  bundle_ascendant: {
    id: "bundle_ascendant", name: "The Ascendant Holding", shelf: "mythical", priceUD: 210000,
    // ⚑ RULED 2026-09-16 (caP'n): the words change, not the contents — the
    // price is getting a rework of its own. It is the YARD's risen set.
    blurb: "The risen yard: four Aethereal Pillars, the Throne, the Rift Door, the Lyt Spire and THE RISE " +
           "over all of it. She packed one. She is not expecting to sell it.",
    contents: { aethereal_pillar: 4, throne_of_dawgs: 1, rift_door: 1, lyt_spire: 1, sky_the_rise: 1 },
  },
};

export const BUNDLE_IDS = Object.keys(BUNDLES);

// ── THE SAME PAIR, FOR CRATES ───────────────────────────────────────
// A bundle is priced as one thing, so it needs its own pair rather than
// the sum of its parts. ⚠ `acquireBundle` must charge `bundlePriceOf`,
// NOT `b.priceUD` — reading the raw field is how the crate stayed at
// full price while every single on the same shelf went to ✹1.
export const bundleListPriceOf = (bundleId) =>
  BUNDLES[bundleId] ? (Number(BUNDLES[bundleId].priceUD) || 0) : null;

export const bundlePriceOf = (bundleId) => {
  const list = bundleListPriceOf(bundleId);
  if (list === null) return null;
  if (list === 0) return 0;
  return TESTING ? TESTING_PRICE_UD : list;
};

// ⚑ COMPUTED, NEVER TYPED. What the contents would cost one at a time.
export function bundleValue(bundleId) {
  const b = BUNDLES[bundleId];
  if (!b) return 0;
  let sum = 0;
  for (const [itemId, n] of Object.entries(b.contents || {})) {
    // ⚠ listPriceOf, NOT priceOf. This is the crate's WORTH, and under
    // the testing till `priceOf` returns ✹1 for everything — which would
    // make every bundle's "saving" read as a loss.
    sum += (listPriceOf(itemId) || 0) * (Number(n) || 0);
  }
  return sum;
}

export function bundleSaving(bundleId) {
  const b = BUNDLES[bundleId];
  if (!b) return { amount: 0, percent: 0 };
  const value = bundleValue(bundleId);
  const amount = Math.max(0, value - (bundleListPriceOf(bundleId) || 0));
  return { amount, value, percent: value > 0 ? Math.round((amount / value) * 100) : 0 };
}

export const bundlesOnShelf = (shelf) =>
  BUNDLE_IDS.map((id) => BUNDLES[id]).filter((b) => b.shelf === shelf);

export const bundleItemCount = (bundleId) =>
  Object.values(BUNDLES[bundleId]?.contents || {}).reduce((a, n) => a + (Number(n) || 0), 0);

// ════════════════════════════════════════════════════════════════════
// ⭐ THE FREE SHELF  —  the bootstrap
// ────────────────────────────────────────────────────────────────────
// A new Aenarchist arrives with zero Dawgs and no way to earn any: no
// door to get into the yard, no bed to plant. So the holding is founded
// with these, granted once, free.
//
// ⚠ AND FREE MEANS FREE — a free grant NEVER READS A BALANCE (L30). It
// cannot fail on funds because it never asks about funds. That is the
// whole reason a free shelf is the safe way to exercise an acquisition
// path before the ledger is server-authoritative.
// ════════════════════════════════════════════════════════════════════
export const FOUNDING_GRANT = {
  plank_door: 2,      // one for the house, one for the yard
  crop_bed: 2,
  stone_path: 4,
  lantern_post: 1,
};
export const FOUNDING_SKY    = "sky_default";
export const FOUNDING_GROUND = "ground_grass";
export const FOUNDING_FLOOR  = "floor_marble";
export const FOUNDING_WALL   = "wall_stone";
export const FOUNDING_LIGHT  = "light_candle";
// every worn default, for the audit and the founding grant
export const FOUNDING_WORN = [FOUNDING_SKY, FOUNDING_GROUND, FOUNDING_FLOOR, FOUNDING_WALL, FOUNDING_LIGHT];
// ⭐ THE WELCOME (2026-09-15). A new holding used to open on ✹0 and a shop
// with every button disabled and no line saying why; the first Dawg was
// five minutes away in a bed the player had not been told to plant. So a
// founding now brings a small purse and a companion at heel. ⚑ ONE-TIME,
// by construction — it rides the same `founded` flag as the crate — so it
// is a gift and not a faucet (L8), and Dawgs are acquire-only anyway (L42).
// ⚑ DIAL: the purse. Under the testing till it buys 120 things; at real
// prices it is one lantern and change.
export const FOUNDING_PURSE_UD  = 120;
// A core-cast being — the species kit is parked (OW-22), so this must be
// one of the 12 lineages `ieBeingModels.jsx` can draw today.
export const FOUNDING_COMPANION = "spectral_tiger";

// ════════════════════════════════════════════════════════════════════
// ⭐ auditCatalogue()  —  THE SCRIPTED ROSTER CHECK
// ────────────────────────────────────────────────────────────────────
// Called once at boot in development. It is here because every content
// file this Empire has ever shipped eventually grew a row that pointed
// at nothing, and the cheapest possible moment to find that is the
// first second of the session rather than the moment a player clicks it.
//
// Returns [] when clean. Never throws — a catalogue problem must not be
// able to white-screen a holding.
// ════════════════════════════════════════════════════════════════════
export function auditCatalogue() {
  const problems = [];
  const say = (m) => problems.push(m);

  for (const [id, it] of Object.entries(ITEMS)) {
    if (it.id !== id) say(`ITEMS.${id}: row's own id is "${it.id}"`);
    if (!SHELVES.includes(it.shelf)) say(`${id}: shelf "${it.shelf}" is not one of the three`);
    if (!Object.values(KIND).includes(it.kind)) say(`${id}: unknown kind "${it.kind}"`);
    if (!Number.isFinite(it.priceUD) || it.priceUD < 0) say(`${id}: priceUD must be a non-negative number`);

    // THE THREE SHELVES, mechanically.
    if (it.shelf === "mythical") {
      if (it.art) say(`${id}: a Mythical must NOT carry its own art — it is its Legendary, risen (L23)`);
      if (!it.ascendedFrom) say(`${id}: a Mythical must carry ascendedFrom`);
      else {
        const from = ITEMS[it.ascendedFrom];
        if (!from) say(`${id}: ascendedFrom "${it.ascendedFrom}" does not exist`);
        else {
          if (from.shelf !== "legendary") say(`${id}: ascends from "${it.ascendedFrom}", which is ${from.shelf}, not legendary`);
          if (from.kind !== it.kind) say(`${id}: ascends from a ${from.kind}, but is a ${it.kind}`);
        }
      }
    } else {
      if (it.ascendedFrom) say(`${id}: only a Mythical may carry ascendedFrom`);
      if (!it.art) say(`${id}: needs an art recipe`);
    }

    if (!artOf(id)) say(`${id}: resolves to no drawable art`);

    if (it.kind === KIND.GROWER) {
      if (!it.grow) say(`${id}: a grower with no grow block`);
      else {
        if (!(it.grow.ripenMs > 0)) say(`${id}: ripenMs must be positive`);
        if (!(it.grow.yieldUD > 0)) say(`${id}: yieldUD must be positive`);
        if (!(it.grow.capCycles > 0)) say(`${id}: capCycles must be positive`);
      }
    } else if (it.grow) {
      say(`${id}: carries a grow block but is not a grower`);
    }

    // ⭐ OW-34 · shape and layer
    if (it.size !== undefined) {
      if (!Array.isArray(it.size) || it.size.length !== 2 || !it.size.every((n) => Number.isInteger(n) && n >= 1 && n <= 4)) {
        say(`${id}: size must be [w, d], whole tiles 1–4`);
      }
    }
    if (it.layer !== undefined && !LAYERS.includes(it.layer)) say(`${id}: unknown layer "${it.layer}"`);
    if (it.layer === LAYER.FLOOR || it.layer === LAYER.WALL) {
      if (!it.walkOver) say(`${id}: a ${it.layer}-layer piece must be walkOver`);
      if (it.stackTop != null) say(`${id}: nothing stacks on a ${it.layer}-layer piece — stackTop must be null`);
      if (it.kind !== KIND.PROP) say(`${id}: only a plain prop may be ${it.layer}-layer`);
    }
    if (it.layer === LAYER.WALL && (it.areas || []).some((a) => a !== AREAS.HALL)) {
      say(`${id}: a wall piece hangs in the Hall only — the yard has no walls`);
    }
    if (it.shelf === "mythical" && it.ascendedFrom && ITEMS[it.ascendedFrom] && !isWorn(id)) {
      const from = ITEMS[it.ascendedFrom];
      if (layerOf(id) !== layerOf(from.id) || sizeLabel(id) !== sizeLabel(from.id)) {
        say(`${id}: a risen piece keeps its Legendary's shape and layer`);
      }
    }
    if (it.kind === KIND.HALL_LIGHT && !moodOf(id)) say(`${id}: a light resolves to no mood`);
    if (it.kind === KIND.HALL_LAYOUT && !it.ascendedFrom && typeof it.layout !== "string") say(`${id}: a layout names its plan in \`layout\``);
    if (it.kind !== KIND.HALL_LAYOUT && it.layout !== undefined) say(`${id}: only a layout carries \`layout\``);

    if (isWorn(id)) {
      if (it.areas && it.areas.length) say(`${id}: a ${it.kind} is worn, not placed — areas must be empty`);
    } else if (!it.areas || !it.areas.length) {
      say(`${id}: placeable but has no areas`);
    }
  }

  for (const [id, b] of Object.entries(BUNDLES)) {
    if (b.id !== id) say(`BUNDLES.${id}: row's own id is "${b.id}"`);
    if (!SHELVES.includes(b.shelf)) say(`${id}: shelf "${b.shelf}" is not one of the three`);
    if (!Number.isFinite(b.priceUD) || b.priceUD <= 0) say(`${id}: a bundle needs a positive price`);
    const entries = Object.entries(b.contents || {});
    if (!entries.length) say(`${id}: an empty bundle`);
    for (const [itemId, n] of entries) {
      if (!ITEMS[itemId]) say(`${id}: contains "${itemId}", which is not an item`);
      if (!Number.isInteger(n) || n < 1) say(`${id}: "${itemId}" count must be a positive integer`);
    }
    // A bundle that costs MORE than its parts is not a bundle.
    if (bundleValue(id) > 0 && b.priceUD >= bundleValue(id)) {
      say(`${id}: priced at ${b.priceUD} but its parts cost ${bundleValue(id)} — that is not a saving`);
    }
  }

  // ⚠ A FOUNDING ITEM DOES NOT HAVE TO BE FREE, and an earlier version
  // of this audit wrongly insisted it did. THE FREE SHELF law is about
  // PURCHASES never reading a balance; a founding grant is a GIFT and
  // reads no balance either way. Two crop beds have a sticker price and
  // are still the right thing to found a holding with — without them a
  // new Aenarchist has no way to earn their first Dawg at all.
  for (const [itemId, n] of Object.entries(FOUNDING_GRANT)) {
    if (!ITEMS[itemId]) say(`FOUNDING_GRANT: "${itemId}" is not an item`);
    else if (isWorn(itemId)) say(`FOUNDING_GRANT: "${itemId}" is worn, not stocked — use FOUNDING_SKY/GROUND`);
    if (!Number.isInteger(n) || n < 1) say(`FOUNDING_GRANT: "${itemId}" count must be a positive integer`);
  }
  for (const [id, kind] of [[FOUNDING_FLOOR, KIND.HALL_FLOOR], [FOUNDING_WALL, KIND.HALL_WALL], [FOUNDING_LIGHT, KIND.HALL_LIGHT]]) {
    if (!ITEMS[id]) say(`founding finish "${id}" is not an item`);
    else if (ITEMS[id].kind !== kind) say(`founding finish "${id}" is not a ${kind}`);
  }
  for (const [id, it] of Object.entries(ITEMS)) {
    if ((it.kind === KIND.HALL_FLOOR || it.kind === KIND.HALL_WALL) && !it.finish?.tint) say(`${id}: a finish needs finish.tint`);
  }
  if (!ITEMS[FOUNDING_SKY])    say(`FOUNDING_SKY "${FOUNDING_SKY}" is not an item`);
  else if (ITEMS[FOUNDING_SKY].kind !== KIND.SKY) say(`FOUNDING_SKY "${FOUNDING_SKY}" is not a sky`);
  if (!ITEMS[FOUNDING_GROUND]) say(`FOUNDING_GROUND "${FOUNDING_GROUND}" is not an item`);
  else if (ITEMS[FOUNDING_GROUND].kind !== KIND.GROUND) say(`FOUNDING_GROUND "${FOUNDING_GROUND}" is not a ground`);

  return problems;
}

const mineriumCatalogueApi = {
  SHELVES, SHELF_META, KIND, AREAS, BARN_HOURS, LAYER, LAYERS,
  layerOf, sizeOf, sizeLabel, turnsInQuarters, moodOf, FOUNDING_LIGHT,
  ITEMS, ITEM_IDS, BUNDLES, BUNDLE_IDS,
  artOf, isAscended, priceOf, isWorn, itemsOnShelf, itemsOfKind,
  bundleValue, bundleSaving, bundlesOnShelf, bundleItemCount,
  FOUNDING_GRANT, FOUNDING_SKY, FOUNDING_GROUND, FOUNDING_FLOOR, FOUNDING_WALL, FOUNDING_WORN,
  FOUNDING_PURSE_UD, FOUNDING_COMPANION, WORN_FIELD, auditCatalogue,
};

export default mineriumCatalogueApi;
